import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

type InfiniteQueryState = Pick<
  UseInfiniteQueryResult,
  'fetchNextPage' | 'hasNextPage' | 'isFetchingNextPage'
>;

/**
 * Wraps an infinite query's next-page request for a list's end-reached callback.
 *
 * A list reports its end whenever it is empty or short, and that is exactly the state of a
 * query that failed with no pages. An unguarded request then fetches the first page again,
 * the failure re-renders the list, the list reports its end again, and the cycle repeats
 * without bound. Ask only when the query says another page exists and that page is not already
 * being fetched. A background refetch does not block the request, because a list reports its
 * end once and a dropped report is not repeated until the content changes.
 */
export function useFetchNextPageWhenIdle({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: InfiniteQueryState): () => void {
  return useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage().catch(() => undefined);
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
}
